import { useCallback, type Dispatch, type RefObject, type SetStateAction } from 'react';

interface InsertResult {
    text: string;
    caret: number;
}

export function insertAtSelection(
    text: string,
    insert: string,
    selectionStart: number,
    selectionEnd: number
): InsertResult {
    return {
        text: text.slice(0, selectionStart) + insert + text.slice(selectionEnd),
        caret: selectionStart + insert.length
    };
}

// Inserts at the caret (replacing any selection) and puts the caret after the insert;
// appends when the textarea isn't mounted yet.
export function useComposerInsert(
    composerRef: RefObject<HTMLTextAreaElement>,
    composerText: string,
    setComposerText: Dispatch<SetStateAction<string>>
): (insert: string) => void {
    return useCallback(
        (insert: string) => {
            const textarea = composerRef.current;
            if (!textarea) {
                setComposerText((prev) => prev + insert);
                return;
            }
            const { text, caret } = insertAtSelection(
                composerText,
                insert,
                textarea.selectionStart ?? composerText.length,
                textarea.selectionEnd ?? composerText.length
            );
            setComposerText(text);
            // Re-focus and reposition the caret after React renders the new value.
            requestAnimationFrame(() => {
                textarea.focus();
                textarea.setSelectionRange(caret, caret);
            });
        },
        [composerRef, composerText, setComposerText]
    );
}
